import { useEffect, useState, useCallback } from "react";
import { api, apiError, resolveMedia } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Image as ImageIcon, Loader2, Pencil, Plus, Check, Search } from "lucide-react";
import { toast } from "sonner";

function MediaPicker({ open, onClose, onSelect }) {
  const [media, setMedia] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/media?status=APPROVED").then((r) => setMedia(r.data)).catch(() => setMedia([]));
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Select an image from the Media Library</DialogTitle></DialogHeader>
        {media === null ? <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {media.map((m) => (
              <button key={m.id} onClick={() => onSelect(m)} data-testid="picker-media-item"
                className="border border-border rounded-lg overflow-hidden hover:border-primary transition-colors">
                <div className="aspect-square bg-muted"><img src={resolveMedia(m.thumbnail)} alt={m.altText} className="w-full h-full object-cover" /></div>
                <p className="text-[11px] p-1.5 truncate">{m.title}</p>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function WebsiteCMS() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("cms:manage");
  const [sections, setSections] = useState(null);
  const [services, setServices] = useState(null);
  const [pages, setPages] = useState(null);
  const [brand, setBrand] = useState(null);
  const [picker, setPicker] = useState(null); // {type, id?}
  const [svcDialog, setSvcDialog] = useState(false);
  const [svcForm, setSvcForm] = useState({ published: true });
  const [pageForm, setPageForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [sec, svc, pg, br] = await Promise.all([
      api.get("/website-sections"), api.get("/services"), api.get("/website-pages"), api.get("/brand-settings"),
    ]);
    setSections(sec.data); setServices(svc.data); setPages(pg.data); setBrand(br.data || {});
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const onPickMedia = async (media) => {
    const p = picker; setPicker(null);
    if (p.type === "section") {
      try { await api.put(`/website-sections/${p.id}/media`, { media_id: media.id }); toast.success("Image updated"); load(); }
      catch (e) { toast.error(apiError(e)); }
    } else if (p.type === "logo") {
      setBrand((b) => ({ ...b, logo_url: media.url, brand: { ...(b.brand || {}), logo_url: media.url } }));
    } else if (p.type === "favicon") {
      setBrand((b) => ({ ...b, brand: { ...(b.brand || {}), favicon_url: media.url } }));
    } else if (p.type === "ogimage") {
      setBrand((b) => ({ ...b, seo: { ...(b.seo || {}), og_image: media.url } }));
    }
  };

  const saveService = async () => {
    try {
      if (svcForm.id) await api.put(`/services/${svcForm.id}`, svcForm);
      else await api.post("/services", svcForm);
      toast.success("Saved"); setSvcDialog(false); setSvcForm({ published: true }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveBrand = async () => {
    setSaving(true);
    try {
      const b = brand.brand || {};
      await api.put("/brand-settings", {
        org_name: b.display_name || brand.org_name, tagline: brand.tagline, mission: brand.mission,
        phone: brand.phone, email: brand.email, address: brand.address,
        logo_url: b.logo_url || brand.logo_url, brand: b, seo: brand.seo || {},
      });
      toast.success("Brand settings saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const savePage = async () => {
    try {
      await api.put(`/website-pages/${pageForm.id}`, pageForm);
      toast.success("Page SEO saved"); setPageForm(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const b = brand?.brand || {};
  const setB = (k, v) => setBrand({ ...brand, brand: { ...b, [k]: v } });
  const setBrandField = (k, v) => setBrand({ ...brand, [k]: v });
  const setSeo = (k, v) => setBrand({ ...brand, seo: { ...(brand.seo || {}), [k]: v } });
  const setSocial = (k, v) => setBrand({ ...brand, brand: { ...b, social: { ...(b.social || {}), [k]: v } } });

  return (
    <div data-testid="cms-page">
      <PageHeader title="Website CMS" subtitle="Manage brand, public content, imagery and per-page SEO." />
      <Tabs defaultValue="brand">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="brand" data-testid="tab-brand">Brand Settings</TabsTrigger>
          <TabsTrigger value="sections" data-testid="tab-sections">Homepage Media</TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services">Services</TabsTrigger>
          <TabsTrigger value="seo" data-testid="tab-seo">SEO Pages</TabsTrigger>
        </TabsList>

        {/* Brand */}
        <TabsContent value="brand">
          {brand === null ? <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
            <div className="grid lg:grid-cols-2 gap-6 max-w-4xl">
              <div className="border border-border rounded-lg bg-card p-6 space-y-4">
                <h3 className="font-heading font-semibold">Identity</h3>
                <div className="space-y-1.5"><Label>Display Brand Name</Label><Input value={b.display_name || brand.org_name || ""} disabled={!canManage} onChange={(e) => setB("display_name", e.target.value)} data-testid="brand-name" /></div>
                <div className="space-y-1.5"><Label>Tagline</Label><Input value={brand.tagline || ""} disabled={!canManage} onChange={(e) => setBrandField("tagline", e.target.value)} data-testid="brand-tagline" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Logo</Label>
                    <div className="flex items-center gap-2">
                      <div className="w-14 h-14 rounded-md border border-border bg-muted flex items-center justify-center overflow-hidden">
                        {(b.logo_url || brand.logo_url) ? <img src={resolveMedia(b.logo_url || brand.logo_url)} alt="logo" className="w-full h-full object-contain" /> : <ImageIcon className="w-5 h-5 text-muted-foreground" />}
                      </div>
                      {canManage && <Button variant="outline" size="sm" onClick={() => setPicker({ type: "logo" })} data-testid="pick-logo">Change</Button>}
                    </div>
                  </div>
                  <div className="space-y-1.5"><Label>Favicon</Label>
                    <div className="flex items-center gap-2">
                      <div className="w-14 h-14 rounded-md border border-border bg-muted flex items-center justify-center overflow-hidden">
                        {b.favicon_url ? <img src={resolveMedia(b.favicon_url)} alt="favicon" className="w-full h-full object-contain" /> : <ImageIcon className="w-5 h-5 text-muted-foreground" />}
                      </div>
                      {canManage && <Button variant="outline" size="sm" onClick={() => setPicker({ type: "favicon" })} data-testid="pick-favicon">Change</Button>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="border border-border rounded-lg bg-card p-6 space-y-4">
                <h3 className="font-heading font-semibold">Contact & Social</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Phone</Label><Input value={brand.phone || ""} disabled={!canManage} onChange={(e) => setBrandField("phone", e.target.value)} data-testid="brand-phone" /></div>
                  <div className="space-y-1.5"><Label>Email</Label><Input value={brand.email || ""} disabled={!canManage} onChange={(e) => setBrandField("email", e.target.value)} data-testid="brand-email" /></div>
                </div>
                <div className="space-y-1.5"><Label>Address</Label><Input value={brand.address || ""} disabled={!canManage} onChange={(e) => setBrandField("address", e.target.value)} data-testid="brand-address" /></div>
                <div className="grid grid-cols-2 gap-3">
                  {["facebook", "instagram", "linkedin", "twitter"].map((k) => (
                    <div key={k} className="space-y-1.5"><Label className="capitalize">{k}</Label><Input value={(b.social || {})[k] || ""} disabled={!canManage} onChange={(e) => setSocial(k, e.target.value)} data-testid={`brand-${k}`} /></div>
                  ))}
                </div>
              </div>

              <div className="border border-border rounded-lg bg-card p-6 space-y-4 lg:col-span-2">
                <h3 className="font-heading font-semibold">Default SEO</h3>
                <div className="space-y-1.5"><Label>Default Title</Label><Input value={(brand.seo || {}).default_title || ""} disabled={!canManage} onChange={(e) => setSeo("default_title", e.target.value)} data-testid="seo-default-title" /></div>
                <div className="space-y-1.5"><Label>Default Meta Description</Label><Textarea value={(brand.seo || {}).default_description || ""} disabled={!canManage} onChange={(e) => setSeo("default_description", e.target.value)} data-testid="seo-default-desc" /></div>
                <div className="space-y-1.5"><Label>Default Open Graph Image</Label>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-14 rounded-md border border-border bg-muted overflow-hidden">{(brand.seo || {}).og_image && <img src={resolveMedia(brand.seo.og_image)} alt="og" className="w-full h-full object-contain" />}</div>
                    {canManage && <Button variant="outline" size="sm" onClick={() => setPicker({ type: "ogimage" })} data-testid="pick-ogimage">Change</Button>}
                  </div>
                </div>
                {canManage && <div className="pt-1"><Button onClick={saveBrand} disabled={saving} data-testid="brand-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Brand Settings"}</Button></div>}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Sections */}
        <TabsContent value="sections">
          {sections === null ? <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {sections.map((s) => (
                <div key={s.id} className="border border-border rounded-lg bg-card overflow-hidden" data-testid="cms-section">
                  <div className="aspect-video bg-muted relative">
                    {s.image_url ? <img src={resolveMedia(s.image_url)} alt={s.title} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageIcon className="w-8 h-8" /></div>}
                  </div>
                  <div className="p-4">
                    <p className="font-heading font-semibold">{s.title}</p>
                    <p className="text-xs text-muted-foreground mb-3">Used on: {s.title}</p>
                    {canManage && <Button variant="outline" size="sm" className="w-full" onClick={() => setPicker({ type: "section", id: s.id })} data-testid="change-image-btn"><ImageIcon className="w-4 h-4 mr-1.5" /> Change image</Button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Services */}
        <TabsContent value="services">
          {canManage && <Button className="mb-4" onClick={() => { setSvcForm({ published: true }); setSvcDialog(true); }} data-testid="service-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Add Service</Button>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(services || []).map((s) => (
              <div key={s.id} className="border border-border rounded-lg bg-card p-4" data-testid="cms-service">
                <div className="flex items-start justify-between">
                  <p className="font-heading font-semibold">{s.name}</p>
                  {canManage && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setSvcForm(s); setSvcDialog(true); }} data-testid="service-edit"><Pencil className="w-3.5 h-3.5" /></Button>}
                </div>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-3">{s.description}</p>
                <span className={`inline-flex items-center gap-1 text-xs mt-3 ${s.published ? "text-success" : "text-muted-foreground"}`}>
                  {s.published && <Check className="w-3 h-3" />} {s.published ? "Published" : "Hidden"}
                </span>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* SEO Pages */}
        <TabsContent value="seo">
          {pages === null ? <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
            <div className="space-y-3">
              {pages.map((p) => (
                <div key={p.id} className="border border-border rounded-lg bg-card p-4 flex items-center justify-between gap-4" data-testid="cms-seo-page">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-heading font-semibold capitalize">{p.name || p.slug}</span>
                      <code className="text-xs text-muted-foreground">{p.url}</code>
                      {p.noindex && <StatusBadge value="noindex" />}
                    </div>
                    <p className="text-sm text-muted-foreground truncate mt-0.5">{p.seo_title}</p>
                  </div>
                  {canManage && <Button variant="outline" size="sm" onClick={() => setPageForm({ ...p })} data-testid="edit-seo-page"><Pencil className="w-4 h-4 mr-1.5" /> Edit SEO</Button>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <MediaPicker open={!!picker} onClose={() => setPicker(null)} onSelect={onPickMedia} />

      {/* Service dialog */}
      <Dialog open={svcDialog} onOpenChange={setSvcDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{svcForm.id ? "Edit" : "New"} Service</DialogTitle></DialogHeader>
          <div className="space-y-3.5">
            <div className="space-y-1.5"><Label>Name</Label><Input value={svcForm.name || ""} onChange={(e) => setSvcForm({ ...svcForm, name: e.target.value })} data-testid="service-name" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={svcForm.description || ""} onChange={(e) => setSvcForm({ ...svcForm, description: e.target.value })} data-testid="service-desc" /></div>
            <div className="flex items-center gap-2"><Switch checked={!!svcForm.published} onCheckedChange={(v) => setSvcForm({ ...svcForm, published: v })} data-testid="service-published" /><Label>Published</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setSvcDialog(false)}>Cancel</Button><Button onClick={saveService} data-testid="service-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Page SEO dialog */}
      <Dialog open={!!pageForm} onOpenChange={(o) => !o && setPageForm(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>SEO · {pageForm?.name}</DialogTitle></DialogHeader>
          {pageForm && (
            <div className="space-y-3.5">
              <div className="space-y-1.5"><Label>Page Title (H1)</Label><Input value={pageForm.h1 || ""} onChange={(e) => setPageForm({ ...pageForm, h1: e.target.value })} data-testid="page-h1" /></div>
              <div className="space-y-1.5"><Label>SEO Title</Label><Input value={pageForm.seo_title || ""} onChange={(e) => setPageForm({ ...pageForm, seo_title: e.target.value })} data-testid="page-seo-title" /></div>
              <div className="space-y-1.5"><Label>Meta Description</Label><Textarea value={pageForm.meta_description || ""} onChange={(e) => setPageForm({ ...pageForm, meta_description: e.target.value })} data-testid="page-meta" /></div>
              <div className="space-y-1.5"><Label>SEO Slug</Label><Input value={pageForm.slug || ""} onChange={(e) => setPageForm({ ...pageForm, slug: e.target.value })} data-testid="page-slug" /></div>
              <div className="space-y-1.5"><Label>Canonical URL</Label><Input value={pageForm.canonical || ""} onChange={(e) => setPageForm({ ...pageForm, canonical: e.target.value })} data-testid="page-canonical" /></div>
              <div className="space-y-1.5"><Label>Open Graph Title</Label><Input value={pageForm.og_title || ""} onChange={(e) => setPageForm({ ...pageForm, og_title: e.target.value })} data-testid="page-og-title" /></div>
              <div className="space-y-1.5"><Label>Open Graph Description</Label><Textarea value={pageForm.og_description || ""} onChange={(e) => setPageForm({ ...pageForm, og_description: e.target.value })} data-testid="page-og-desc" /></div>
              <div className="flex items-center justify-between border border-border rounded-md px-3 py-2">
                <div><Label className="text-sm">No-index this page</Label><p className="text-xs text-muted-foreground">Hide from search engines</p></div>
                <Switch checked={!!pageForm.noindex} onCheckedChange={(v) => setPageForm({ ...pageForm, noindex: v })} data-testid="page-noindex" />
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setPageForm(null)}>Cancel</Button><Button onClick={savePage} data-testid="page-save">Save SEO</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
